import getIdentifier from 'getIdentifier';
import { toFilterPlate } from 'migrateScope';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

type SourceFilter = NonNullable<InsightsChartSpec['source']['filters']>[number];

// Agent number operators → the client's FilterRangeOperator.
const RANGE_OPERATOR: Record<string, FilterRangeOperator> = {
  gte: 'is more than or equal to',
  gt: 'is more than',
  lte: 'is less than or equal to',
  lt: 'is less than',
  between: 'is between',
};

// Translate one agent filter ({ field, kind, operator, values }) into a
// FixedFilter, mirroring the backend's buildAgentFilterInput semantics. Returns
// undefined for shapes we can't represent (skipped rather than guessed).
const toFixedFilter = (filter: SourceFilter): FixedFilter | undefined => {
  const base = {
    id: getIdentifier(),
    mode: 'editing' as const,
    field: filter.field,
  };

  if (filter.kind === 'text') {
    const textFilter: TextFilter = {
      ...base,
      fieldType: 'text',
      keywordValues: filter.values.map(String),
      isExclude: filter.operator === 'is not',
    };
    return textFilter;
  }

  if (filter.kind === 'boolean') {
    const raw = filter.values[0];
    const booleanFilter: BooleanFilter = {
      ...base,
      fieldType: 'boolean',
      booleanValue: raw === true || raw === 'true',
      isExclude: filter.operator === 'is not',
    };
    return booleanFilter;
  }

  if (filter.kind === 'number') {
    const operator = RANGE_OPERATOR[filter.operator];
    if (!operator) {
      return undefined;
    }
    const nums = filter.values.map(Number);
    const numberFilter: NumberFilter = {
      ...base,
      fieldType: 'float',
      rangeValue: {
        operator,
        valueA: nums[0],
        ...(operator === 'is between' ? { valueB: nums[1] } : {}),
      },
    };
    return numberFilter;
  }

  return undefined;
};

// Build a gadget `scope` (FilterPlateType[]) from a chat chart's source filters,
// so a saved chart reproduces the filtered data the user saw — not an unfiltered
// version. Reuses the app's toFilterPlate converter.
//
// The Explore modal feeds the same output into DRILL-DOWNS rather than scope, so
// the user can remove a filter the agent chose (FilterPlates only renders
// drill-down chips as removable). Same shape either way.
//
// NOTE: a filter shape toFixedFilter can't represent is dropped silently, so the
// result can be narrower than the chat's. Surfacing that to the user would need
// this to also report what it dropped.
const buildScopeFromSource = (
  filters: InsightsChartSpec['source']['filters'],
): FilterPlateType[] =>
  (filters ?? [])
    .map(toFixedFilter)
    .filter((filter): filter is FixedFilter => filter !== undefined)
    .map(toFilterPlate);

export default buildScopeFromSource;
